import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { ModelName } from '@casops/store/api';
import {
  ConfigPackageRequest,
  ConfigurationGroupPackageModel,
  useGetConfigurationGroupQuery,
} from '@casops/store/apis/configurationGroupsApi';
import { ConnectivitySnackbar } from '@dmc/ui';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import useConfigPackageColumnDefs from './useConfigPackageColumnDefs';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { Permission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';

/**
 * Augment the package model type to include the configuration group ID so that each row has the
 * necessary data to form API requests independently.
 */
export interface ConfigPackageListModel
  extends ConfigurationGroupPackageModel,
    ConfigPackageRequest {}

type Model = ConfigPackageListModel;

const modelName = ModelName.ConfigurationGroupPackage;
const sidePanel = SidePanel.ManageConfigurationGroupPackage;
const permission = Permission.Admin;

export function ConfigurationGroupPackages() {
  const { t } = useTranslation();
  const { configGroupId } = useParams();

  const columnDefs = useConfigPackageColumnDefs();

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const queryParams = configGroupId ? { id: Number(configGroupId) } : skipToken;

  const { data, ...requestRemainder } = useGetConfigurationGroupQuery(queryParams, {
    refetchOnMountOrArgChange: 60,
  });

  const { packages } = data ?? {};
  const fetchError = requestRemainder.error;

  // Augment the returned list to include the configGroupId in each set of row data.
  const augmentedPackages = useMemo<Model[] | undefined>(() => {
    if (typeof configGroupId !== 'undefined') {
      return packages?.map((item: ConfigurationGroupPackageModel) => ({
        ...item,
        configGroupId: Number(configGroupId),
      }));
    }
    return undefined;
  }, [packages, configGroupId]);

  const { hasData, noDataDescription } = useTableData<Model>({
    data: augmentedPackages,
    ...requestRemainder,
    noResultsDescription,
  });

  const sliceKey = `${modelName}--${configGroupId}`; // Create a unique sliceKey for each config group.

  return (
    <>
      <CollectionView<Model>
        actions={
          <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
        }
        sliceKey={sliceKey}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={augmentedPackages}
        hasData={hasData}
        noDataDescription={noDataDescription}
        includeHiddenColumnsInQuickFilter={true}
      />
      <ConnectivitySnackbar
        error={fetchError}
        message={t('Model.Fetch.Error', { modelName: ModelName.ConfigurationGroup })}
        title={t('Error')}
      />
    </>
  );
}

export default ConfigurationGroupPackages;
